'use client';

import { forwardRef } from 'react';

import Link from 'next/link';

import { useBrand } from '~/components/brand-provider';
import { resolveCrossBrandHref } from '~/lib/routing/resolve-app-url';

type NextLinkProps = React.ComponentPropsWithoutRef<typeof Link>;

export interface BrandLinkProps extends Omit<NextLinkProps, 'href'> {
  /**
   * The internal app path to link to (optionally with `?query`/`#hash`).
   * Resolved through {@link resolveCrossBrandHref} against the current brand:
   * a path owned by the current brand (or shared) stays relative; a path owned
   * by the OTHER brand is routed through the authenticated cross-brand handoff
   * endpoint (`/api/cross-brand/handoff?to=…`) so a logged-in user stays signed
   * in across the Flow ↔ Ballee hop. The handoff endpoint itself passes
   * anonymous users straight through to the destination.
   */
  to: string;
}

/**
 * Brand-aware `<Link>`.
 *
 * A thin wrapper over `next/link` that runs every `to` path through
 * `resolveCrossBrandHref` using the brand from the `BrandProvider` context
 * (seeded from the `x-brand` header at the root layout). Use it for any
 * internal link that *might* cross the Flow ↔ Ballee surface boundary: a
 * same-brand/shared path stays relative, and a cross-brand path is routed
 * through the authenticated handoff endpoint so a logged-in user is not forced
 * to re-login on the other domain.
 *
 * Client components only (it reads `useBrand()`). For **server components**,
 * call `resolveCrossBrandHref(path, await getBrand())` directly and pass the
 * result to a plain `<Link href={…}>` — there is no client context on the
 * server. (Use `resolveAppUrl` instead only when you specifically want the
 * bare cross-brand URL *without* the auth handoff, e.g. an anonymous-only CTA.)
 *
 * All other `next/link` props (`className`, `prefetch`, `replace`, `scroll`,
 * `onClick`, `aria-*`, children, …) are forwarded unchanged, and `ref` is
 * passed through so `<Button asChild><BrandLink …></Button>` works.
 *
 * @example
 * // On Flow, renders an absolute ballee.co URL; on Ballee, relative.
 * <BrandLink to={`/competitions/${slug}/apply?applicationId=${id}`}>
 *   Continue
 * </BrandLink>
 */
export const BrandLink = forwardRef<HTMLAnchorElement, BrandLinkProps>(
  function BrandLink({ to, ...rest }, ref) {
    const brand = useBrand();
    return <Link ref={ref} href={resolveCrossBrandHref(to, brand)} {...rest} />;
  },
);
